/**
 * Custom hook for managing Adyen checkout lifecycle
 */
import { SelectedPaymentMethod } from '@/types/checkout';
import AdyenCheckout from '@adyen/adyen-web';
import type UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ADYEN_CARD_CONFIG, ADYEN_CONFIG } from '../config/adyen.config';
import { SessionState } from '../payments.types';
import { AdyenCheckoutInstance, AdyenPaymentResult, AdyenSessionState, type KlarnaVariant } from '../types/adyen.types';
import { buildPaymentComponentConfig } from '../utils/paymentConfig.utils';

interface UseAdyenCheckoutParams {
  session?: SessionState;
  paymentMethodType?: SelectedPaymentMethod['type'];
  amount: number;
  googlePayLineItems?: google.payments.api.DisplayItem[];
  brands?: string[];
  klarnaVariant?: KlarnaVariant;
  onComplete?: (result: AdyenPaymentResult) => void;
  onError?: (error: unknown) => void;
}

interface UseAdyenCheckoutReturn {
  checkout: UIElement | null;
  loading: boolean;
  submitting: boolean;
  error: Error | null;
  sessionState: AdyenSessionState;
  paymentContainerRef: (node: HTMLDivElement | null) => void;
  handleSubmit: () => void;
}

export const useAdyenCheckout = ({
  session,
  paymentMethodType,
  amount,
  googlePayLineItems,
  brands,
  klarnaVariant,
  onComplete,
  onError,
}: UseAdyenCheckoutParams): UseAdyenCheckoutReturn => {
  const [checkout, setCheckout] = useState<UIElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sessionState, setSessionState] = useState<AdyenSessionState>('active');
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  const checkoutInstanceRef = useRef<UIElement | null>(null);
  // Guards against double-submission during StrictMode double-invocation or rapid session re-renders
  const hasSubmittedRef = useRef(false);

  // Store callbacks and array/object props in refs to avoid re-initializing the SDK
  // when consumers pass inline values (which create new references every render)
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const googlePayLineItemsRef = useRef(googlePayLineItems);
  const brandsRef = useRef(brands);
  const sessionRef = useRef(session);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    googlePayLineItemsRef.current = googlePayLineItems;
  }, [googlePayLineItems]);

  useEffect(() => {
    brandsRef.current = brands;
  }, [brands]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Reset submission guard and loading state when session changes
  useEffect(() => {
    hasSubmittedRef.current = false;
    setLoading(true);
    setSubmitting(false);
    setError(null);
  }, [session?.id, paymentMethodType, klarnaVariant]);

  // Callback ref for payment container — clears on unmount to prevent stale references
  const paymentContainerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node);
  }, []);

  // Initialize and mount Adyen checkout
  useEffect(() => {
    const session = sessionRef.current;
    if (!session || !containerElement || !paymentMethodType) {
      return;
    }

    let isMounted = true;

    const initCheckout = async () => {
      try {
        setSessionState('active');
        setError(null);

        // Build payment method specific configuration
        const componentConfig = buildPaymentComponentConfig({
          paymentMethodType,
          amount,
          googlePayLineItems: googlePayLineItemsRef.current,
          brands: brandsRef.current,
          klarnaVariant,
        });

        if (!componentConfig) {
          if (isMounted) {
            setError(new Error(`Unsupported payment method type: ${paymentMethodType}`));
            setLoading(false);
            setSessionState('disabled');
          }
          return;
        }

        // Initialize Adyen Checkout
        const adyenCheckout: AdyenCheckoutInstance = await AdyenCheckout({
          paymentMethodsConfiguration: {
            card: ADYEN_CARD_CONFIG,
          },
          locale: ADYEN_CONFIG.LOCALE,
          environment: ADYEN_CONFIG.ENVIRONMENT,
          clientKey: ADYEN_CONFIG.CLIENT_KEY,
          session: {
            id: session.id,
            sessionData: session.data,
          },
          onError: (sdkError) => {
            if (!isMounted) return;
            console.error('Adyen checkout error:', sdkError);
            setSubmitting(false);
            setSessionState('disabled');
            setError(sdkError instanceof Error ? sdkError : new Error(String(sdkError)));
            onErrorRef.current?.(sdkError);
          },
          onPaymentCompleted: (result) => {
            if (!isMounted) return;
            setSubmitting(false);
            onCompleteRef.current?.(result);
          },
        });

        // Create and mount the payment component
        const component = adyenCheckout.create(componentConfig.paymentComponent, componentConfig.options || {});

        component?.mount?.(containerElement);
        checkoutInstanceRef.current = component;

        // Auto-submit for non-manual payment methods (e.g. Swish, Klarna).
        // Wallet methods (Google Pay / Apple Pay) use manualSubmit and submit on the
        // user's button gesture instead, so they are excluded by this guard.
        // rAF defers the call to after the browser has painted, ensuring the component
        // is fully mounted and its internal state is ready before submitting
        if (!componentConfig.manualSubmit && !hasSubmittedRef.current) {
          hasSubmittedRef.current = true;
          requestAnimationFrame(() => {
            component?.submit?.();
          });
        }

        if (isMounted) {
          setCheckout(component);
          setLoading(false);
        }
      } catch (initError) {
        console.error('Failed to initialize Adyen checkout:', initError);
        if (isMounted) {
          setError(initError instanceof Error ? initError : new Error(String(initError)));
          setLoading(false);
          setSessionState('disabled');
        }
      }
    };

    initCheckout();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (checkoutInstanceRef.current) {
        try {
          checkoutInstanceRef.current.unmount?.();
        } catch (e) {
          console.error('Error unmounting Adyen component:', e);
        }
        checkoutInstanceRef.current = null;
      }
    };
  }, [session?.id, paymentMethodType, klarnaVariant, containerElement, amount]);

  // Handle manual submit for card/form-based payment methods
  // submit() is always called — even when invalid — so the SDK can render its own
  // inline validation errors. setSubmitting is only set when the form is actually
  // valid to avoid showing a loading state during a failed validation attempt.
  const handleSubmit = useCallback(() => {
    if (checkout) {
      if (checkout.isValid) {
        setSubmitting(true);
      }
      checkout.submit?.();
    }
  }, [checkout]);

  return {
    checkout,
    loading,
    submitting,
    error,
    sessionState,
    paymentContainerRef,
    handleSubmit,
  };
};
